import { useRef, useEffect, useState } from "react";
import { Divider } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import numberToWords from "number-to-words";
import { useNavigate, useLocation } from "react-router";
import QrCodeGenerator from "views/commonComponents/QrCode/QrCodeGenerator";
import { useSelector } from "react-redux";

function A4PrintTaxVAt() {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, tableData, qrData, typ } = location.state;
  const company = useSelector((state) => state.common.companyInfo);
  const companyLogo = company.logo ? JSON.parse(company.logo)?.img : null;
  const decimalCount = useSelector((state) => state.common.decimalCount);
  const primaryCurrency = useSelector((state) => state.common.primaryCurrency);
  const subCurrency = useSelector((state) => state.common.subCurrency);
  
  const [pages, setPages] = useState([]);
  const [calculated, setCalculated] = useState(false);
  const [rowHeights, setRowHeights] = useState([]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(-2);
    },
  });

  useEffect(() => {
    const calculateRowHeights = () => {
      const heights = tableData.map(item => {
        const description = item.ItemNameTextField || '';
        const lineHeight = 20;
        const maxWidth = 300;
        const avgCharsPerLine = maxWidth / 8;
        const lines = Math.ceil(description.length / avgCharsPerLine);
        return Math.max(40, lines * lineHeight);
      });
      setRowHeights(heights);
    };

    calculateRowHeights();
  }, [tableData]);

  useEffect(() => {
    const calculatePages = () => {
      if (rowHeights.length === 0) return;
      
      const pageHeight = 1122;
      const headerHeight = 350;
      const footerHeight = 250;
      const availableHeight = pageHeight - headerHeight - footerHeight;
      
      const newPages = [];
      let currentPage = [];
      let currentHeight = 0;

      rowHeights.forEach((height, index) => {
        if (currentHeight + height > availableHeight && currentPage.length > 0) {
          newPages.push(currentPage);
          currentPage = [index];
          currentHeight = height;
        } else {
          currentPage.push(index);
          currentHeight += height;
        }
      });

      if (currentPage.length > 0) {
        newPages.push(currentPage);
      }

      setPages(newPages);
      setCalculated(true);
    };

    if (rowHeights.length > 0) {
      calculatePages();
    }
  }, [rowHeights]);

  useEffect(() => {
    if (company && calculated) {
      const timeoutId = setTimeout(() => {
        handlePrint();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, calculated]);

  function convertNumberToWordsWithCurrency(number) {
    const paisaAmount = ((number || 0) * 100) % 100;
    const words = numberToWords?.toWords(Math?.floor((number || 0)));
    const paisaInWords = numberToWords?.toWords(paisaAmount);
    return `${words} ${primaryCurrency} and  ${paisaInWords} ${subCurrency}`;
  }

  const amount =
    typ === "PR" || typ === "PB" ? data.NTot : data.BillNetTotalField;
  const currency = "Saudi Riyal";
  const result = convertNumberToWordsWithCurrency(amount, currency);

  const generateSections = () => {
    return pages.map((pageIndices, pageIndex) => {
      const sectionItems = pageIndices.map(index => ({
        ...tableData[index],
        printSino: index + 1
      }));
      return renderSection(sectionItems, pageIndex, pageIndex > 0);
    });
  };

  const renderSection = (sectionItems, sectionIndex, isContinued) => {
    return (
      <div
        key={`section-${sectionIndex}`}
        style={{
          marginTop: sectionIndex !== 0 ? "1rem" : "0",
          pageBreakBefore: isContinued ? "always" : "auto",
          border: "0px ",
          height: "100%",
        }}
      >
        <div style={{ height: "100%" }}>
          <div style={{ border: "1px solid black" }}>
            <div
              style={{
                justifyContent: "space-between",
                display: "flex",
                padding: "5px",
              }}
            >
              <div>
                <div style={{ fontWeight: "bold" }}>{company?.Name}</div>
                <div style={{ fontWeight: 600 }}>{company?.Place}</div>
                <div>{company?.Add1}</div>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "50%",
                    }}
                  >
                    <div>CR</div>
                    <div>:</div>
                  </div>
                  <div
                    style={{
                      width: "50%",
                      paddingLeft: "3px",
                    }}
                  >
                    {company?.additional_no}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "50%",
                    }}
                  >
                    <div>VAT No</div>
                    <div>:</div>
                  </div>
                  <div
                    style={{
                      width: "50%",
                      paddingLeft: "3px",
                    }}
                  >
                    {company?.Add2}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "50%",
                    }}
                  >
                    <div>Phone</div>
                    <div>:</div>
                  </div>
                  <div
                    style={{
                      width: "50%",
                      paddingLeft: "3px",
                    }}
                  >
                    {company?.Ph}
                  </div>
                </div>
              </div>

              <div
                style={{
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  fontWeight: "bold",
                  gap: "5px",
                }}
              >
                <div style={{ width: "150px", height: "100px" }}>
                  <img
                    alt=""
                    style={{ width: "100%", height: "100%" }}
                    src={companyLogo}
                  />
                </div>
                <div>
                  {typ === "SB" ? (
                    <div
                      style={{ display: "flex", gap: "5px", fontSize: "16px" }}
                    >
                      <div>VAT INVOICE</div>
                      <div>فاتورة ضريبة</div>
                    </div>
                  ) : typ === "SR" ? (
                    <div
                      style={{
                        display: "flex",
                        gap: "5px",
                        fontSize: "16px",
                      }}
                    >
                      <div>SALES RETURN</div>
                      <div>عائد المبيعات</div>
                    </div>
                  ) : typ === "PB" ? (
                    <div
                      style={{
                        display: "flex",
                        gap: "5px",
                        fontSize: "16px",
                      }}
                    >
                      <div>PURCHASE BILL</div>
                      <div> فاتورة الشراء</div>
                    </div>
                  ) : typ === "OF" ? (
                    <div
                      style={{
                        display: "flex",
                        gap: "5px",
                        fontSize: "16px",
                      }}
                    >
                      <div>ORDER FORM</div>
                      <div> نموذج الطلب </div>
                    </div>
                  ) : typ === "B2C" ? (
                    <div
                      style={{
                        display: "flex",
                        gap: "5px",
                        fontSize: "16px",
                      }}
                    >
                      <div>SIMPLIFIED TAX INVOICE</div>
                      <div> فاتورة ضريبية مبسطة </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        gap: "5px",
                        fontSize: "16px",
                      }}
                    >
                      <div>PURCHASE RETURN</div>
                      <div> عودة شراء</div>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ width: "180px" }}>
                <div style={{ textAlign: "right", fontWeight: "bold" }}>
                  {company?.name_ar}
                </div>
                <div style={{ textAlign: "right", fontWeight: 600 }}>
                  {company?.building_no_ar}
                </div>
                <div style={{ textAlign: "right" }}>
                  {`${company?.street_ar} ${company.city_ar} `}
                </div>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      width: "50%",
                      paddingRight: "3px",
                      textAlign: "right",
                    }}
                  >
                    {company?.additional_no_ar}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "50%",
                    }}
                  >
                    <div>:</div>
                    <div>CR</div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      width: "50%",
                      paddingRight: "3px",
                      textAlign: "right",
                    }}
                  >
                    {company?.tax_no_ar}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "50%",
                    }}
                  >
                    <div>:</div>
                    <div>VAT No</div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      width: "50%",
                      paddingRight: "3px",
                      textAlign: "right",
                    }}
                  >
                    {company?.Ph}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "50%",
                    }}
                  >
                    <div>:</div>
                    <div>Phone</div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", width: "100%", padding: "5px" }}>
              <div style={{ width: "50%" }}>
                <table
                  style={{
                    borderCollapse: "collapse",
                    border: "1px solid #8c8b8b",
                  }}
                >
                  <tbody>
                    <tr>
                      <th
                        style={{
                          borderCollapse: "collapse",
                          border: "1px solid #8c8b8b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "120px",
                            padding: "3px",
                            fontSize: "16px",
                          }}
                        >
                          <div>To</div>
                          <div>لى</div>
                        </div>
                      </th>
                      <th
                        style={{
                          textAlign: "start",
                          width: "80%",
                          border: "1px solid #8c8b8b",
                          fontSize: "15px",
                        }}
                      >
                        {data?.BillPartyName}
                      </th>
                    </tr>
                    <tr>
                      <th
                        style={{
                          borderCollapse: "collapse",
                          border: "1px solid #8c8b8b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "120px",
                            padding: "3px",
                            fontSize: "16px",
                          }}
                        >
                          <div>Address</div>
                          <div>عنوان</div>
                        </div>
                      </th>
                      <th
                        style={{
                          textAlign: "start",
                          width: "80%",
                          border: "1px solid #8c8b8b",
                          fontSize: "15px",
                        }}
                      >
                        {data?.Address1}
                      </th>
                    </tr>
                    <tr>
                      <th
                        style={{
                          borderCollapse: "collapse",
                          border: "1px solid #8c8b8b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "120px",
                            padding: "3px",
                            fontSize: "16px",
                          }}
                        >
                          <div>VAT No.</div>
                          <div></div>
                        </div>
                      </th>
                      <th
                        style={{
                          textAlign: "start",
                          width: "80%",
                          border: "1px solid #8c8b8b",
                          fontSize: "15px",
                        }}
                      >
                        {data?.TinNo}
                      </th>
                    </tr>
                    <tr>
                      <th
                        style={{
                          borderCollapse: "collapse",
                          border: "1px solid #8c8b8b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "120px",
                            padding: "3px",
                            fontSize: "16px",
                          }}
                        >
                          <div>Ph</div>
                          <div>جوال</div>
                        </div>
                      </th>
                      <th
                        style={{
                          textAlign: "start",
                          width: "80%",
                          border: "1px solid #8c8b8b",
                          fontSize: "15px",
                        }}
                      >
                        {data?.Ph}
                      </th>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ width: "50%" }}>
                <table
                  style={{
                    borderCollapse: "collapse",
                    border: "1px solid #8c8b8b",
                    lineHeight: "28px",
                  }}
                >
                  <tbody>
                    <tr>
                      <th
                        style={{
                          borderCollapse: "collapse",
                          border: "1px solid #8c8b8b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "170px",
                            padding: "3px",
                            fontSize: "13px",
                          }}
                        >
                          <div>INVOICE NO</div>
                          <div>رقم الفاتورة </div>
                        </div>
                      </th>
                      <th
                        style={{
                          textAlign: "start",
                          width: "80%",
                          border: "1px solid #8c8b8b",
                          fontSize: "15px",
                        }}
                      >
                        <div
                          style={{
                            justifyContent: "space-evenly",
                            display: "flex",
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              width: "50%",
                            }}
                          >
                            {data?.BillNo}
                          </div>
                        </div>
                      </th>
                    </tr>
                    <tr>
                      <th
                        style={{
                          borderCollapse: "collapse",
                          border: "1px solid #8c8b8b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",

                            padding: "3px",
                            fontSize: "13px",
                          }}
                        >
                          <div>INVOICE DATE</div>
                          <div>تاريخ</div>
                        </div>
                      </th>
                      <th
                        style={{
                          textAlign: "start",
                          width: "80%",
                          border: "1px solid #8c8b8b",
                          fontSize: "15px",
                        }}
                      >
                        <div
                          style={{
                            justifyContent: "space-evenly",
                            display: "flex",
                          }}
                        >
                          <div>
                            {new Date(data?.BillDate).toLocaleDateString()}
                          </div>
                          <div>
                            {new Date(data?.BillDate).toLocaleTimeString()}
                          </div>
                        </div>
                      </th>
                    </tr>
                    <tr>
                      <th
                        style={{
                          borderCollapse: "collapse",
                          border: "1px solid #8c8b8b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",

                            padding: "3px",
                            fontSize: "13px",
                          }}
                        >
                          <div>Remarks</div>
                          <div>ملاحظات</div>
                        </div>
                      </th>
                      <th
                        style={{
                          textAlign: "start",
                          width: "80%",
                          border: "1px solid #8c8b8b",
                          fontSize: "15px",
                        }}
                      ></th>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: "45rem" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      border: "1px solid #8c8b8b",
                      padding: "0.6rem",
                    }}
                  >
                    <div>SI NO</div>
                    <div>صيني</div>
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      border: "1px solid #8c8b8b",
                      width: "30%",
                      padding: "0.6rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div>Description</div>
                      <div>البـيــــــــــــان</div>
                    </div>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid #8c8b8b",
                      padding: "0.6rem",
                    }}
                  >
                    <div>Qty</div>
                    <div>الكمية</div>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid #8c8b8b",
                      padding: "0.6rem",
                    }}
                  >
                    <div>Rate</div>
                    <div>السعر</div>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid #8c8b8b",
                      padding: "0.6rem",
                    }}
                  >
                    <div>Unit</div>
                    <div>وحدة</div>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid #8c8b8b",
                      padding: "0.6rem",
                    }}
                  >
                    <div>Amount</div>
                    <div>كمية</div>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid #8c8b8b",
                      padding: "0.6rem",
                    }}
                  >
                    <div>Tax%</div>
                    <div>نسبة الضريبة</div>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid #8c8b8b",
                      padding: "0.6rem",
                    }}
                  >
                    <div>Tax</div>
                    <div>ضريبة</div>
                  </th>

                  <th
                    style={{
                      textAlign: "right",
                      border: "1px solid #8c8b8b",
                      padding: "0.6rem",
                    }}
                  >
                    <div>Total</div>
                    <div>المجموع</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sectionItems.map((prod, index) => (
                  <tr key={index} style={{ height: rowHeights[prod.printSino - 1] || 'auto' }}>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid #8c8b8b",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.printSino}
                    </td>
                    <td
                      style={{
                        textAlign: "left",
                        borderRight: "1px solid #8c8b8b",
                        padding: "5px",
                        wordWrap: "break-word",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.ItemNameTextField}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid #8c8b8b",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.qty}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid #8c8b8b",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {typ === "PR" || typ === "PB"
                        ? parseFloat(prod.Cost).toFixed(decimalCount)
                        : parseFloat(prod.Rate1).toFixed(decimalCount)}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid #8c8b8b",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.Typ}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid #8c8b8b",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {typ === "PR" || typ === "PB"
                        ? (prod.Cost * prod.qty).toFixed(decimalCount)
                        : (prod.Rate1 * prod.qty).toFixed(decimalCount)}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid #8c8b8b",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.Tax1}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid #8c8b8b",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {parseFloat(prod.taxAmt).toFixed(decimalCount)}
                    </td>

                    <td
                      style={{
                        textAlign: "right",
                        borderRight: "1px solid #8c8b8b",
                        paddingRight: "5px",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.total.toFixed(decimalCount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Divider sx={{ borderBottom: "0.3px solid grey" }} />
          </div>

          <div
            style={{
              height: "320px",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                width: "100%",
                borderTop: "1px solid #8c8b8b",
                alignItems: "center",
                padding: "10px",
              }}
            >
              <div style={{ width: "150px" }}>
                <QrCodeGenerator qrData={qrData} />
              </div>
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "end",
                  padding: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <table
                  style={{
                    lineHeight: "33px",
                  }}
                >
                  <tbody>
                    <tr>
                      <td>
                        <div
                          style={{
                            width: "600px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ width: "150px", fontWeight: "bold" }}>
                            Total Excld. VAT
                          </div>
                          <div
                            style={{
                              width: "150px",
                              textAlign: "end",
                              fontWeight: "bold",
                            }}
                          >
                            الإجمالي غير شامل ضريبة :
                          </div>
                          <div style={{ width: "150px", textAlign: "end" }}>
                            {typ === "PR" || typ === "PB"
                              ? (data?.Total - data?.TItTaxAmt).toFixed(
                                  decimalCount
                                )
                              : (data?.BillTotalField - data?.TItTaxAmt).toFixed(
                                  decimalCount
                                )}
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div
                          style={{
                            width: "600px",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <div style={{ width: "150px", fontWeight: "bold" }}>
                            Discount
                          </div>
                          <div
                            style={{
                              width: "150px",
                              textAlign: "end",
                              fontWeight: "bold",
                            }}
                          >
                            الخصـم :
                          </div>
                          <div style={{ width: "150px", textAlign: "end" }}>
                            {typ === "PR" || typ === "PB"
                              ? data.DiscAmt?.toFixed(decimalCount)
                              : data?.BillDiscAmtField?.toFixed(decimalCount)}
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div
                          style={{
                            width: "600px",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <div style={{ width: "150px", fontWeight: "bold" }}>
                            Vat {company?.tax === 0 ? null : company?.tax}%
                          </div>
                          <div
                            style={{
                              width: "150px",
                              textAlign: "end",
                              fontWeight: "bold",
                            }}
                          >
                            {" "}
                            ضريبة القيمة المضافة{" "}
                            {company?.tax === 0 ? null : company?.tax}%:
                          </div>
                          <div style={{ width: "150px", textAlign: "end" }}>
                            {typ === "PR" || typ === "PB"
                              ? data?.Tax.toFixed(decimalCount)
                              : data?.TItTaxAmt.toFixed(decimalCount)}
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div
                          style={{
                            width: "600px",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <div style={{ width: "150px", fontWeight: "bold" }}>
                            Total Amt (Incl.VAT)
                          </div>
                          <div
                            style={{
                              width: "150px",
                              textAlign: "end",
                              fontWeight: "bold",
                            }}
                          >
                            الإجمالي شامل ضريبة :
                          </div>
                          <div style={{ width: "150px", textAlign: "end" }}>
                            {typ === "PR" || typ === "PB"
                              ? data.NTot.toFixed(decimalCount)
                              : parseFloat(data?.BillNetTotalField).toFixed(
                                  decimalCount
                                )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: "10px",
              }}
            >
              <div>
                <div>Salesman Signature</div>
                <div style={{ textAlign: "center", fontWeight: "bold" }}>
                  توقيع البائع
                </div>
                <div style={{ paddingTop: "10px" }}>
                  ..................................
                </div>
              </div>
              <div
                style={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div>Amount in Words : {result}</div>
              </div>
              <div>
                <div>Reciever's Signature</div>
                <div style={{ textAlign: "center", fontWeight: "bold" }}>
                  توقيع المستلم
                </div>
                <div style={{ paddingTop: "10px" }}>
                  ...................................
                </div>
              </div>
            </div>
            <div
              style={{
                background: "#000099",
                color: "white",
                marginTop: "10px",
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  padding: "10px",
                  fontWeight: 500,
                }}
              >
                <div style={{ fontWeight: "bold" }}>
                  {`${company?.name_ar} ${company?.building_no_ar} 
                  ${company?.street_ar} ${company?.city_ar} `}
                </div>
                <div>
                  {`${company?.Name} ${company?.Place} ${company?.Web}`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const sections = generateSections();
  
  return (
    <div>
      <div
        ref={componentRef}
        style={{
          background: "white",
          border: "1px solid black",
          margin: "1rem",
          height: "100%",
        }}
      >
        {sections}
      </div>
    </div>
  );
}

export default A4PrintTaxVAt;